import { Component } from "react";
import { observer } from "mobx-react";

import { taskPlanService } from "../services/taskPlanService.ts";
import { ImgUp } from "./Images/ImgUp.tsx";
import { ImgDown } from "./Images/ImgDown.tsx";
import { ImgCancel } from "./Images/ImgCancel.tsx";
import { ImgTrash } from "./Images/ImgTrash.tsx";
import { ImgDocumentCheck } from "./Images/ImgDocumentCheck.tsx";
import { ImgInProgress } from "./Images/ImgInProgress.tsx";

type TaskPlanPanelProps = {
  chatId: string | null;
};

@observer
export class TaskPlanPanel extends Component<TaskPlanPanelProps> {
  private _lastLoadedChatId: string | null = null;

  componentDidMount() {
    this._loadIfNeeded();
  }

  componentDidUpdate() {
    this._loadIfNeeded();
  }

  // Fetch the plan once per chat switch; live updates afterwards arrive over the WS 'plan:updated' event.
  private _loadIfNeeded() {
    const { chatId } = this.props;
    if (chatId && chatId !== this._lastLoadedChatId) {
      this._lastLoadedChatId = chatId;
      void taskPlanService.loadPlan(chatId);
    }
  }

  private _onToggleCollapse = () => {
    const { chatId } = this.props;
    if (chatId) taskPlanService.toggleCollapsed(chatId);
  };

  private _onClose = () => {
    const { chatId } = this.props;
    if (chatId) taskPlanService.close(chatId);
  };

  private _onClear = () => {
    const { chatId } = this.props;
    if (chatId && window.confirm('Clear the task plan for this chat? This cannot be undone.')) {
      void taskPlanService.clearPlan(chatId);
    }
  };

  render() {
    const { chatId } = this.props;
    if (!chatId) return null;

    const plan = taskPlanService.getPlan(chatId);
    if (!plan || plan.steps.length === 0 || taskPlanService.isClosed(chatId)) return null;

    const collapsed = taskPlanService.isCollapsed(chatId);
    const doneCount = plan.steps.filter(s => s.status === 'completed').length;

    return (
      <div className={`task-plan-panel${collapsed ? ' collapsed' : ''}`}>
        <div className="task-plan-header">
          <button className="task-plan-toggle" onClick={this._onToggleCollapse} title={collapsed ? 'Expand plan' : 'Collapse plan'}>
            {collapsed ? <ImgUp size={14} /> : <ImgDown size={14} />}
          </button>
          <span className="task-plan-title">Plan</span>
          <span className="task-plan-progress">{doneCount}/{plan.steps.length} done</span>
          <div className="task-plan-actions">
            <button className="task-plan-action-btn" onClick={this._onClear} title="Clear plan">
              <ImgTrash size={14} />
            </button>
            <button className="task-plan-action-btn" onClick={this._onClose} title="Close">
              <ImgCancel size={14} />
            </button>
          </div>
        </div>
        {!collapsed && (
          <div className="task-plan-steps">
            {plan.steps.map(step => (
              <div key={step.id} className={`task-plan-step task-plan-step--${step.status}`}>
                <span className="task-plan-step-icon">
                  {step.status === 'completed' && <ImgDocumentCheck size={14} />}
                  {step.status === 'in-progress' && <ImgInProgress size={14} />}
                  {step.status === 'not-started' && <span className="task-plan-step-dot" />}
                </span>
                <span className="task-plan-step-title">{step.title}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
}
